/**
 * Flip cards - Figma `3840:63005`, flipped state `3840:63691`.
 *
 * A four-column grid where a lead spans the first three columns and the rest are tiles.
 * The lead is heading and body in one large size (h2), the button pinned to the foot.
 * A pale tile shows only its title; clicking it turns the tile over to a blue face
 * carrying the body. Photographs are mixed into the same grid and do not turn.
 *
 * **Only one is ever face-up, and the previous one turns back first.** Clicking a second
 * tile does not swap them in place: the open one rotates closed, and only when it has
 * finished does the new one rotate open. That is the behaviour the design asks for, and
 * it is the reason this component has a timer rather than a single piece of state - the
 * two rotations have to be sequenced, not simultaneous.
 *
 * `FLIP_MS` is the rotation, and the pause between the two is the same, so the gesture
 * reads as one movement rather than two overlapping ones. If it is ever tuned, the CSS
 * duration below and this constant have to move together.
 *
 * **Which tiles turn is decided by the content**: a card with an image is a photograph,
 * one without is a tile - the rule `meetings-grid` and `visa-feature` already use. A
 * tile with a `url` gets a button on its front face instead of turning, which is how the
 * design draws "Aktualne Oferty Pracy".
 *
 * **Every tile is a square**, the photographs too (they were 4:5 until 2026-10-30) - the
 * designer's call, so a row of tiles and photographs stays level.
 *
 * The numbering (01, 02, ...) counts the tiles that turn, in order, and skips the
 * photographs - as the file does.
 *
 * Accessibility: both faces are in the DOM at all times, so the one facing away is
 * `aria-hidden`, or a screen reader would read every tile twice. It is **not** hidden
 * with `visibility` - see the note on `Face`, which is where the first version went
 * wrong. The tile itself is a `button` with `aria-expanded`, because that is what it is -
 * a disclosure that happens to be animated - and under `prefers-reduced-motion` the
 * rotation is dropped.
 *
 * **The tile number is dimmed, not faded past reading.** It was 60% on both faces,
 * which measures 4.09:1 on the pale tile and 3.19:1 on the blue one - under the 4.5:1
 * AA asks of body text. 70% and 80% are the first steps that clear it (5.53 and 4.58)
 * and keep the number quieter than the title, which is the point of dimming it.
 */
import { useEffect, useRef, useState } from 'react';
import { CtaLink, Eyebrow, SectionShell, withHighlight } from '../primitives';
import { Image, SIZES } from '../../components/ui/Image';
import { Button } from '../../components/ui/Button';
import { useSite } from '../../lib/site-context';
import type { Card, FlipCardsSection } from '../../types/sections';

const FLIP_MS = 320;

const FACE = 'absolute inset-0 flex flex-col justify-between rounded-l p-6';

/**
 * One face of a card.
 *
 * **`backface-visibility` does the hiding, not `visibility`.** The first version toggled
 * `visibility` on the two faces the moment the state changed, so between 0 and 90 degrees
 * the front was already hidden and the back was still turned away - the card went blank
 * for half the rotation. The faces are now always visible and the browser hides whichever
 * one is facing away, which is the whole point of the property.
 *
 * **The back face carries its own `rotateY(180deg)`.** Without it the face is turned away
 * at the end of the rotation rather than towards the reader, so the text never appears -
 * which is what happened, and what a screenshot of the closed state does not show.
 *
 * `-webkit-` on both properties: Safari still wants the prefix for `transform-style`, and
 * this is exactly the kind of thing that works everywhere the author tested and nowhere
 * else.
 *
 * A `span`, not a `div`: this sits inside a `button`, whose content model is phrasing
 * content. The layout comes from `flex`, so nothing is lost.
 */
function Face({ hidden, className, rotated = false, children }: {
  hidden: boolean; className: string; rotated?: boolean; children: React.ReactNode;
}) {
  return (
    <span
      aria-hidden={hidden}
      className={`${FACE} ${className}`}
      style={{
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: rotated ? 'rotateY(180deg)' : 'rotateY(0deg)'
      }}
    >
      {children}
    </span>
  );
}

function Tile({ card, number, open, onToggle }: {
  card: Card; number: number | null; open: boolean; onToggle: () => void;
}) {
  const { t } = useSite();
  // --- a photograph: in the grid, but not part of the interaction ------------
  if (card.image) {
    return (
      <figure className="m-0 aspect-square overflow-hidden rounded-l bg-surface-sunken">
        <Image
          media={card.image}
          alt={card.image.alternativeText ?? card.title ?? ''}
          sizes={SIZES.quarter}
          className="h-full w-full object-cover"
        />
      </figure>
    );
  }

  const label = number !== null ? String(number).padStart(2, '0') : null;

  // --- a tile that links out instead of turning over ------------------------
  //
  // The file's own Button, primary and without the arrow (6377:88011): 16px, 45 tall.
  // The number beside it and on every tile is body-Medium, 16px.
  if (card.url) {
    return (
      <div className="flex aspect-square flex-col justify-between rounded-l bg-blue-50 p-6">
        <h3 className="text-h4 text-blue-900">{card.title}</h3>
        <div className="flex items-end justify-between gap-4">
          <Button to={card.url}>{card.body || card.label || t('cta.check')}</Button>
          {label ? <span className="text-body font-medium text-blue-900/70">{label}</span> : null}
        </div>
      </div>
    );
  }

  // --- a tile that turns over ----------------------------------------------
  //
  // The title is a `span` rather than a heading: the tile is a disclosure, and the title
  // is the control's own label, not a heading in the document outline. It would also be
  // invalid inside a `button`.
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="group relative aspect-square text-left focus-visible:outline-none"
      style={{ perspective: '1200px' }}
    >
      <span
        className="absolute inset-0 block transition-transform duration-300 motion-reduce:transition-none"
        style={{
          transformStyle: 'preserve-3d',
          WebkitTransformStyle: 'preserve-3d',
          transform: open ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        <Face hidden={open} className="bg-blue-50 text-blue-900 group-hover:bg-blue-100">
          <span className="text-h4">{card.title}</span>
          {label ? <span className="self-end text-body font-medium text-blue-900/70">{label}</span> : null}
        </Face>

        <Face hidden={!open} rotated className="bg-blue-dark-200 text-white">
          <span className="flex flex-col gap-4">
            <span className="text-h4">{card.title}</span>
            {card.body ? <span className="block text-body-sm text-white/90">{card.body}</span> : null}
          </span>
          {label ? <span className="self-end text-body font-medium text-white/80">{label}</span> : null}
        </Face>
      </span>
    </button>
  );
}

export default function FlipCards({ data }: { data: FlipCardsSection }) {
  const cards = data.cards ?? [];
  const [open, setOpen] = useState<number | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  function toggle(index: number) {
    window.clearTimeout(timer.current);
    if (open === index) { setOpen(null); return; }
    if (open === null) { setOpen(index); return; }
    // Close the open one, and open the next only once it has finished turning.
    setOpen(null);
    timer.current = window.setTimeout(() => setOpen(index), FLIP_MS);
  }

  // The numbers in the design count the tiles that turn and skip the photographs.
  let counter = 0;
  const numbers = cards.map((card) => (card.image ? null : ++counter));

  return (
    <SectionShell settings={data.settings} name="flip-cards">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.heading || data.body || data.cta ? (
          <div className="flex flex-col justify-between gap-8 sm:col-span-2 lg:col-span-3">
            {/* One block of large type: the design (6377:88022) sets the heading and the body as
                a single 46px paragraph, 968 wide, with the button at the foot of the row -
                the same treatment as the text panel. */}
            <div className="flex max-w-[968px] flex-col gap-2 text-h3 text-ink lg:text-h2">
              {data.heading ? <h2>{withHighlight(data.heading)}</h2> : null}
              {data.body ? <p className="whitespace-pre-line">{data.body}</p> : null}
            </div>
            {data.cta ? <CtaLink value={data.cta} /> : null}
          </div>
        ) : null}

        {cards.map((card, i) => (
          <Tile key={i} card={card} number={numbers[i]} open={open === i} onToggle={() => toggle(i)} />
        ))}
      </div>
    </SectionShell>
  );
}
